import { ChevronRight, Clock } from 'lucide-react'
import type { PortalTypeKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { FinanceTypeCard } from '@/hooks/useFinanceiroSolicitacoesPage'
import { getPortalTypeCatalog } from '@/components/PortalPj/portalPjTypeCatalog'
import { cn } from '@/utils/cn'

type Props = {
  items: FinanceTypeCard[]
  onSelect: (key: PortalTypeKey) => void
  /** Lista do portal Rocket: ícone, título, descrição e SLA */
  catalog?: boolean
  compact?: boolean
}

const itemBase =
  'group flex w-full gap-4 rounded-xl border border-[#e91e63]/20 bg-white/[0.05] text-left shadow-sm transition-all duration-200 hover:border-[#e91e63]/45 hover:bg-white/[0.08] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e91e63]/30'

export function PortalPjTypeList({ items, onSelect, catalog = false, compact }: Props) {
  if (items.length === 0) {
    return (
      <p className='rounded-xl border border-dashed border-white/15 bg-white/[0.04] px-4 py-8 text-center text-sm text-white/60'>
        Nenhum tipo disponível nesta categoria.
      </p>
    )
  }

  if (catalog) {
    return (
      <ul className='divide-y divide-white/10 overflow-hidden rounded-2xl border border-[#e91e63]/20 bg-white/[0.05] shadow-xl shadow-black/10'>
        {items.map((item) => {
          const meta = getPortalTypeCatalog(item.key)
          const Icon = meta.icon
          return (
            <li key={item.key}>
              <button
                type='button'
                onClick={() => onSelect(item.key as PortalTypeKey)}
                className={cn(itemBase, 'items-start border-0 px-5 py-5 shadow-none hover:shadow-none')}
              >
                <span className='mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#e91e63]/10 text-[#e91e63]'>
                  <Icon size={20} aria-hidden />
                </span>
                <span className='min-w-0 flex-1'>
                  <span className='block text-base font-semibold leading-snug text-white'>
                    {meta.catalogTitle ?? item.label}
                  </span>
                  <span className='mt-2 block text-sm leading-relaxed text-white/60'>
                    {meta.description}
                  </span>
                  <span className='mt-2 flex items-center gap-1.5 text-xs text-white/45'>
                    <Clock size={12} aria-hidden />
                    SLA {item.slaHours}h
                  </span>
                </span>
                <ChevronRight
                  size={20}
                  className='mt-2 shrink-0 text-white/45 transition-transform group-hover:translate-x-0.5 group-hover:text-[#e91e63]'
                  aria-hidden
                />
              </button>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <ul className={cn('space-y-3', !compact && 'sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0')}>
      {items.map((item) => (
        <li key={item.key} className={!compact ? 'sm:contents' : undefined}>
          <button
            type='button'
            onClick={() => onSelect(item.key as PortalTypeKey)}
            className={cn(
              itemBase,
              'items-center px-5 py-4',
              !compact && 'sm:min-h-[104px] sm:flex-col sm:items-start sm:justify-between',
              compact && 'justify-between',
            )}
          >
            <div className='min-w-0 flex-1'>
              <p className='text-base font-semibold leading-snug text-white'>
                {item.label}
              </p>
              <p className='mt-2 flex items-center gap-1.5 text-sm text-white/50'>
                <Clock size={14} aria-hidden />
                SLA {item.slaHours}h
              </p>
            </div>
            <ChevronRight
              size={22}
              className={cn(
                'shrink-0 text-white/45 transition-transform group-hover:translate-x-0.5 group-hover:text-[#e91e63]',
                !compact && 'sm:self-end',
              )}
              aria-hidden
            />
          </button>
        </li>
      ))}
    </ul>
  )
}
